import { ArrayToMapByPredicateWithMap } from "global/Utility/TSFunctions";
import { bcbs_config_attribute, bcbs_config_masterattribute } from "model/CRM";
import {
    IFlexibleField as FAM_IFlexibleField,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    TValue as FAM_TValue
} from "model/FlexibleAttributeModel";
import { EHideReason } from "./EHideReason";
import { IFieldReference } from "./IFieldReference";
import { ILoopDetectionData } from "./ILoopDetectionData";
import { IParentVisibilityNode } from "./IParentVisibilityNode";
import { IParentVisibilityResult } from "./IParentVisibilityResult";

export class ParentVisibilityGraph {

    // all nodes, indexed by their attribute id
    public nodes: Map<string, IParentVisibilityNode> = new Map();

    // all nodes who have no parent (meaning not part of a cycle)
    // we use this to accelerate the pre-processing of rules
    // this is not used at field entry time
    public entryNodes: Map<string, IParentVisibilityNode> = new Map();
    public loopNodeLookup: Map<string, IParentVisibilityNode> = new Map();
}

export class ParentVisibilityGraphProcessor {

    private readonly config: FAM_IGroupConfigurationConfig;
    private readonly graph: ParentVisibilityGraph | null;

    private readonly stack: IParentVisibilityNode[] = [];

    // all nodes, indexed by their attribute id
    public nodes: Map<string, IParentVisibilityNode> = new Map();

    // all nodes who have no parent (meaning not part of a cycle)
    // we use this to accelerate the pre-processing of rules
    // this is not used at field entry time
    public entryNodes: Map<string, IParentVisibilityNode> = new Map();

    public loops: IParentVisibilityNode[][] = [];
    public loopNodeLookup: Map<string, IParentVisibilityNode> = new Map();


    constructor(config: FAM_IGroupConfigurationConfig, graph?: ParentVisibilityGraph) {
        this.config = config;
        this.graph = graph ?? null;

        if (graph) {
            this.nodes = graph.nodes;
            this.entryNodes = graph.entryNodes;
            this.loopNodeLookup = graph.loopNodeLookup;
        }
    }

    // temp storage for building the graph
    initialize(config: FAM_IGroupConfigurationConfig) {

        this.nodes.clear();
        this.entryNodes.clear();
        this.stack.length = 0;

        const maMap = new Map<string, bcbs_config_attribute[]>();
        config.attributes.forEach(a => {
            let attributes = maMap.get(a._bcbs_config_masterattributeid_value!);
            if (!attributes) {
                attributes = [];
                maMap.set(a._bcbs_config_masterattributeid_value!, attributes);
            }
            attributes.push(a);
        });

        // iterate all master attributes
        config.masterattributes.forEach(seedMasterAttribute => {
            const _attributes = maMap.get(seedMasterAttribute.bcbs_config_masterattributeid!);

            _attributes?.forEach(attribute => {
                this.addAttribute(attribute, seedMasterAttribute);
            });

            while (this.stack.length > 0) {
                const node = this.stack.pop();

                if (!node)
                    continue;

                // check if the node has a parent master attribute
                if (node.attribute._bcbs_parentmasterattributeid_value) {
                    // it has a parent master attribute, so we need to 
                    // process the parent and it's child attributes

                    // get the parent master attribute
                    let parentMasterAttribute = config.cache.map_bcbs_config_masterattribute[node.attribute._bcbs_parentmasterattributeid_value!];

                    // add the parent master attribute's child attributes to the stack and graph
                    // let childAttributes = maMap.get(node.masterAttribute.bcbs_config_masterattributeid!); // ?
                    let childAttributes = maMap.get(parentMasterAttribute.bcbs_config_masterattributeid!);

                    childAttributes?.forEach(childAttribute => {
                        const newNode = this.addAttribute(childAttribute, parentMasterAttribute);

                        // link to this attribute and back
                        if (!newNode)
                            return;

                        node.nodesThatControlMyVisibility.push(newNode);
                        newNode?.nodesThatIControlVisibility.push(node);
                    });
                } else {
                    this.entryNodes.set(node?.attribute.bcbs_config_attributeid!, node);
                }
            }
        });

        this.stack.length = 0;

        this.nodes.forEach(node => {
            const t = this.LoopDetectionData.get(node.attribute.bcbs_config_attributeid!)
            if (!t)
                this.detectStronglyConnectedComponents(node);
        })
        this.stack.length = 0;
        this.LoopDetectionData.clear();

        const graph = new ParentVisibilityGraph();
        graph.entryNodes = this.entryNodes;
        graph.nodes = this.nodes;
        graph.loopNodeLookup = this.loopNodeLookup;


        return graph;
    }

    LoopDetectionData = new Map<string, ILoopDetectionData>();
    loopDetectionIndex = 0;

    // if I understand the c# right and what I read from Tarjan:
    // we are trying to find strong components (biggest connected loops that are joined with bridges)
    // then ignore bridges and just use those loops to check unhide the entire loop if all of them are hidden
    // or a field is part of the loop

    // https://emre.me/algorithms/tarjans-algorithm/
    // Bridge : A Bridge (or cut-edge) in graph theory is any edge in a graph whose removal increases the number of connected components.
    // An Articulation Point (or cut-vertex) is any node in a graph whose removal increases the number of connected components.
    // low links, Low Link Value of a node is defined as the smallest id reachable from that node when doing a

    // Steps:
    // 1) do a DFS and id graph(make label for nodes)
    //    ** guaranteeing it to be reproducible (in our case nodes order are based on master attribute)
    // 2) find a low link value
    // 3) find bridges, basically where low link values are not match
    //    ** can look between edge and two vertex

    private detectStronglyConnectedComponents(node: IParentVisibilityNode) {
        const data: ILoopDetectionData = {
            id: node.attribute.bcbs_config_attributeid!,
            index: this.loopDetectionIndex,
            lowLink: this.loopDetectionIndex,
            onStack: true
        }

        this.LoopDetectionData.set(node.attribute.bcbs_config_attributeid!, data);
        this.loopDetectionIndex++;

        this.stack.push(node);

        node.nodesThatIControlVisibility.forEach(child => {

            let childData = this.LoopDetectionData.get(child.attribute.bcbs_config_attributeid!);

            if (!childData) {
                this.detectStronglyConnectedComponents(child);

                childData = this.LoopDetectionData.get(child.attribute.bcbs_config_attributeid!);

                if (!childData)
                    throw new Error('expecting child data');

                data.lowLink = Math.min(data.lowLink, childData.lowLink);
            } else if (childData.onStack) {
                data.lowLink = Math.min(data.lowLink, childData.index);
            }
        });

        if (data.lowLink === data.index) {
            const loop: IParentVisibilityNode[] = [];

            let tempNode: IParentVisibilityNode | null = null;


            do {
                tempNode = this.stack.pop() ?? null;

                if (!tempNode)
                    throw new Error('expecting a tempnode');

                const t = this.LoopDetectionData.get(tempNode?.attribute.bcbs_config_attributeid!)!;
                t.onStack = false;

                loop.push(tempNode)

            } while (node !== tempNode)


            if (loop.length > 1) {
                this.loops.push(loop);

                loop.forEach(loopNode => {
                    this.loopNodeLookup.set(loopNode.attribute.bcbs_config_attributeid!, loopNode);
                });
            }

        }

    }

    private addAttribute(attribute: bcbs_config_attribute, masterAttribute: bcbs_config_masterattribute) {
        // skip if the node has already been added to the graph
        if (this.nodes.has(attribute.bcbs_config_attributeid!)) {
            // return null;
            return this.nodes.get(attribute.bcbs_config_attributeid!);
        }

        const node = {
            attribute: attribute,
            masterAttribute: masterAttribute,
            nodesThatControlMyVisibility: [],
            nodesThatIControlVisibility: []
        };

        this.stack.push(node);

        this.nodes.set(attribute.bcbs_config_attributeid!, node);

        return node;
    }

    processVisibilityOnAttributeChange(
        config: FAM_IGroupConfigurationConfig,
        triggerField: FAM_IFlexibleField,
        fields: FAM_IFlexibleField[],
        fieldMap?: Map<string, IFieldReference>
    ): Map<string, IParentVisibilityResult> {

        const results: Map<string, IParentVisibilityResult> = new Map();

        fieldMap = fieldMap ?? ArrayToMapByPredicateWithMap(fields, eField => eField.configRef.id, (eField, index) => {
            const attribute = config.cache.map_bcbs_config_attribute[eField.configRef.id];

            const masterAttribute = config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];

            const ret: IFieldReference = {
                index: index,
                field: eField,
                attribute: attribute,
                masterAttribute: masterAttribute,
            }
            return ret;
        });

        if (!fieldMap)
            throw new Error("Field map was not available!");

        // process anything that depends on the triggerField
        const triggerNode = this.nodes.get(triggerField.configRef.id);

        // walk the tree
        triggerNode?.nodesThatIControlVisibility.forEach(node => {
            // get the node field
            const fieldRef = fieldMap!.get(node.attribute.bcbs_config_attributeid!);
            if (!fieldRef)
                return;

            this.processNodeVisibility(node, fieldRef, fieldMap!, results);
        });

        return results;
    }


    // map all the fields by their attribute id so we can get the value
    processVisibilityForContext(fieldMap: Map<string, IFieldReference>) {
        const results = new Map<string, IParentVisibilityResult>();

        // process parents and track those we process
        this.entryNodes.forEach(node => {
            // make sure the node exists in the field array
            if (!fieldMap.has(node.attribute.bcbs_config_attributeid!))
                return;

            // get the node field
            const fieldRef = fieldMap.get(node.attribute.bcbs_config_attributeid!);
            if (!fieldRef)
                return;

            // we need to send in an empty results list to ensure that
            // the recursive function re-processes the graph, just in case
            // the previous iteration processed some nodes of this graph
            const tempResults = new Map<string, IFieldReference>();
            this.processNodeVisibility(node, fieldRef, fieldMap, tempResults);
            tempResults.forEach((value, key) => {
                results.set(key, value);
            });
        });

        // process everything didn't process already
        this.nodes.forEach(node => {
            // make sure the node exists in the field array
            if (!fieldMap.has(node.attribute.bcbs_config_attributeid!))
                return;

            // make sure we haven't processed the node already
            if (results.has(node.attribute.bcbs_config_attributeid!))
                return;

            // get the node field
            const fieldRef = fieldMap.get(node.attribute.bcbs_config_attributeid!);
            if (!fieldRef)
                return;

            this.processNodeVisibility(node, fieldRef, fieldMap, results);
        });


        return results;
    }

    private processNodeVisibility(
        node: IParentVisibilityNode,
        fieldRef: IFieldReference,
        fieldMap: Map<string, IFieldReference>,
        results: Map<string, IParentVisibilityResult>
    ): void {
        // TODO determine if we can clear values or not
        // TODO determine if we get into a clear chain that clears all fields

        // use the results map to NOT reprocess nodes
        if (results.has(node.attribute.bcbs_config_attributeid!))
            return;

        // if (node.attribute.bcbs_config_attributeid === '27fbb77c-578d-ec11-b400-0022480a90e0')
        //     debugger;

        const isMemberOfLoop = this.loopNodeLookup.get(node.attribute.bcbs_config_attributeid!);

        // get parent values
        const values: FAM_TValue[] = [];
        node.nodesThatControlMyVisibility.forEach(controller => {
            // filter to only the nodes that are visible
            const controllerFieldRef = fieldMap.get(controller.attribute.bcbs_config_attributeid!);
            if (!controllerFieldRef)
                return;

            // get the visibility of the field as of the trigger
            let visibility: EHideReason = controllerFieldRef.field.visibility || EHideReason.Visible;

            // get the current visibility of the field considering what we've changed thus far
            const result = results.get(controller.attribute.bcbs_config_attributeid!);
            if (result && result.newVisibility !== undefined && result.newVisibility !== null)
                visibility = result.newVisibility;

            // both current and the beginning visibility
            if (visibility <= 0)
                values.push(controllerFieldRef.field.values[0].value);

        });

        // get the compare value
        const compareValue = node.attribute.bcbs_parentattributevalue || "";

        // skip if compare value is empty
        if (compareValue === null || compareValue === undefined || compareValue === "") {
            // if the parent value is null, then we can't process the parent
            // rule, so we assume that it's visible wrt. the parent visibility
            results.set(node.attribute.bcbs_config_attributeid!, {
                ...fieldRef,
                newVisibility: this.applyVisibility(true, fieldRef.field.visibility)
            });
        } else {
            // split by ';'
            // trim each value
            // toLower each value
            // bool we need to convert yes/no to "true"/"false"
            const compareValueExtracts = compareValue.toLowerCase().split(";").map(s => {
                let val = s.trim();
                val = val.replace(/yes/gi, "true");
                val = val.replace(/no/gi, "false");
                return val;
            });

            // NOTE: all parent visibility rules are "Show If.." rules

            // evaluate the parent condition
            let show: boolean = false;

            let bestValue: any = null;
            values.forEach(val => {
                if (val == null)
                    return;

                // TODO inline fieldValueToString, to optimize for performance
                if (val?.type === 'multioptionset') {
                    
                    bestValue = val?.value;

                    for (let i = 0; i < val.options.length; i++) {
                        const option = val.options[i];
                        if (compareValueExtracts.indexOf(option.value.toString()) > -1) {
                            show = true;
                            break;
                        }
                    }
                }
                else {
                    let valStr = fieldValueToString(this.config, val, true);

                    if (valStr)
                        bestValue = valStr;

                    // this should work for all values except for Date
                    show = show || compareValueExtracts.indexOf(valStr) >= 0;
                }

            });

            if (!bestValue)
                if (isMemberOfLoop) {
                    show = true;
                }

            // set the parent visibility for this field
            results.set(node.attribute.bcbs_config_attributeid!, {
                ...fieldRef,
                newVisibility: this.applyVisibility(show, fieldRef.field.visibility)
            });
        }

        // process children
        node.nodesThatIControlVisibility.forEach(child => {
            // get the node field
            const childFieldRef = fieldMap.get(child.attribute.bcbs_config_attributeid!);
            if (!childFieldRef)
                return;

            this.processNodeVisibility(child, childFieldRef, fieldMap, results);
        });
    }

    private applyVisibility(show: boolean, original?: EHideReason) {
        if (original === undefined)
            return show ? EHideReason.Visible : EHideReason.ParentVisibility;
        else if (show)
            return original & ~EHideReason.ParentVisibility; // remove parent bit
        else
            return original | EHideReason.ParentVisibility; // apply parent bit
    }
}

/*
    This function converts a value to its string representation for comparison and logical processing purposes.
    It should not be used for display purposes; use a friendly value for that instead.
*/
export function fieldValueToString(config: FAM_IGroupConfigurationConfig, val: FAM_TValue, lowerCase: boolean) {
    let valStr = '';

    switch (val?.type) {
        case "bool":
            if (val.value)
                valStr = 'true';
            else
                valStr = 'false'
            break;

        case "date":
            valStr = val.value;
            break;

        case "number":
            valStr = val?.value?.toString();
            //tofixed(2) maybe?
            break;

        case "entity":
            valStr = val.id;

            // lets lookup id in optionsetitem if it exists
            const opt = config.cache.map_bcbs_config_optionsetitems[val.id];
            if (opt) {
                valStr = opt.bcbs_value?.toString() ?? '';
            }

            break;

        // note: this is not the optionsetitem, optionsetitem is of entity type
        case "optionset":
            valStr = val?.value?.toString();
            break;

        // emit event needs to have the actual entered value
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/386988
        case "string":
            valStr = lowerCase
                ? val?.value?.toLowerCase()
                : val?.value;
            break;

        case "multioptionset":
            const bcbs_config_optionsetitems = val.options.map(t => config.cache.map_bcbs_config_optionsetitems[t.id])
            if (bcbs_config_optionsetitems.length > 0) {
                valStr = bcbs_config_optionsetitems.map(t => t.bcbs_value).join(';');
            }
            break;

        case undefined:
            break;

        case null:
            break;

        default:
            if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
                throw new Error(`Value type was not detected during visibility check.`);
            }
    }

    return valStr;
}